// react and js
import PropTypes from "prop-types";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { React, useContext } from "react";
import { useTranslation } from "react-i18next";

// mui
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Box, AppBar, IconButton, Typography, Grid, Tooltip } from "@mui/material";

// components
import Iconify from "../../components/Iconify";
import LanguageSelector from "../../components/LanguageSelector";
import backendRequest from "../../components/BackendRequest";

// hooks
import defines from "../../hooks/defines";
import appContext from "../../hooks/appContext";
import { stateStorageInit } from "../../hooks/stateStorageInit";

// i18n
import { localizeExternalUrl } from "../../i18n/languages";

// layouts

// pages

// theme
import { B3Tiny } from "../../theme/styled";

// utils

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function PrivateHeader({ onOpenSidebar }) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  PrivateHeader.propTypes = {
    onOpenSidebar: PropTypes.func,
  };

  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const { stateStorage, setStateStorage } = useContext(appContext);
  const websiteUrl = localizeExternalUrl(
    process.env.REACT_APP_URL_FRONTEND_WEBSITE,
    i18n.language
  );

  const userEmail =
    stateStorage.userInformation &&
    stateStorage.userInformation.account_settings &&
    stateStorage.userInformation.account_settings.config_data &&
    stateStorage.userInformation.account_settings.config_data.config_data_client
      ? stateStorage.userInformation.account_settings.config_data
          .config_data_client.email
      : "";

  const userTooltip = (
    <Tooltip
      title={
        <Box sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: "12px", color: "#737785" }}>
            {t("layout.userMenu.loggedInAs")}
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#2E2E2E" }}>
            {userEmail}
          </Typography>
        </Box>
      }
      placement="bottom-end"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "white",
            color: "#2E2E2E",
            boxShadow: "0px 0px 4px rgba(0,0,0,0.25)",
            borderRadius: "8px",
            px: 1.5,
            py: 1,
            maxWidth: "none",
          },
        },
      }}
    >
      <IconButton
        sx={{
          color: "#2E2E2E",
          borderRadius: "10px",
        }}
      >
        <Iconify
          icon="eva:person-fill"
          minWidth={22}
          minHeight={22}
        />
      </IconButton>
    </Tooltip>
  );

  const handleLogout = () => {
    if (
      localStorage.getItem("myReauthentication") === "false" &&
      navigator.onLine === true
    ) {
      localStorage.setItem("myReauthentication", true);

      let dashboardAccessTokenCopy = null;
      if (stateStorage.userInformation !== "") {
        dashboardAccessTokenCopy =
          stateStorage.userInformation.answer.dashboard_access_token;
      }

      setStateStorage(stateStorageInit);

      localStorage.setItem("myLocalStateStorage", JSON.stringify(""));
      localStorage.removeItem("myLocalStateStorage");

      sessionStorage.setItem("myLocalLastRoute", JSON.stringify(""));
      sessionStorage.removeItem("myLocalLastRoute");

      localStorage.setItem("myLocalSettingStorage", JSON.stringify(""));
      localStorage.removeItem("myLocalSettingStorage");

      localStorage.setItem("myReauthentication", JSON.stringify(""));
      localStorage.removeItem("myReauthentication");

      if (dashboardAccessTokenCopy !== null) {
        const sendData = {
          request_type: "dashboard",
          route_info: "authentication",
          client: {
            mode: "logout",
            dashboard_access_token: dashboardAccessTokenCopy,
          },
        };
        backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData);
      }

      navigate("/");
    }
  };

  return (
    <AppBar
      sx={{
        background: "#F2F2F2",
        boxShadow: "0px 0px 2px rgba(0,0,0,0.5)",
        minHeight: isDesktop
          ? defines.headerHeightDesktop
          : defines.headerHeightMobile,
        maxHeight: isDesktop
          ? defines.headerHeightDesktop
          : defines.headerHeightMobile,
      }}
    >
      <Grid
        container
        sx={{
          position: "relative",
          justifyContent: isDesktop ? "space-between" : "space-between",
          alignItems: "center",
          justifyItems: "center",
          minHeight: isDesktop
            ? defines.headerHeightDesktop
            : defines.headerHeightMobile,
          maxHeight: isDesktop
            ? defines.headerHeightDesktop
            : defines.headerHeightMobile,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        >
          {isDesktop && (
            <Typography variant="topbar" sx={{ whiteSpace: "nowrap" }}>
              {t("layout.topbarTitle")}
            </Typography>
          )}
        </Box>

        {isDesktop ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <RouterLink
                to={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                }}
              >
                <Box
                  component="img"
                  src={process.env.REACT_APP_PATH_ICON_SCALEABLE}
                  sx={{
                    marginTop: "-1px",
                    marginLeft: "22px",
                    width: "38px",
                    height: "38px",
                  }}
                />

                <Typography
                  sx={{
                    marginLeft: "12px",
                    fontSize: "22px",
                    fontWeight: 700,
                    letterSpacing: 0.15,
                    color: "#2E2E2E",
                    whiteSpace: "nowrap",
                  }}
                >
                  {process.env.REACT_APP_NAME_BIZ}
                </Typography>
              </RouterLink>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginRight: "12px" }}>
              <LanguageSelector />
              {userTooltip}
              <B3Tiny variant="outlined" onClick={handleLogout}>
                {t("layout.actions.logout")}
              </B3Tiny>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <RouterLink
                to={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                }}
              >
                <Box
                  component="img"
                  src={process.env.REACT_APP_PATH_ICON_SCALEABLE}
                  sx={{
                    marginTop: "-2px",
                    marginLeft: "16px",
                    width: "38px",
                    height: "38px",
                  }}
                />

                <Typography
                  sx={{
                    marginLeft: "12px",
                    fontSize: "22px",
                    fontWeight: 700,
                    letterSpacing: 0.15,
                    color: "#2E2E2E",
                    whiteSpace: "nowrap",
                  }}
                >
                  {process.env.REACT_APP_NAME_BIZ}
                </Typography>
              </RouterLink>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LanguageSelector />
              {userTooltip}
              <B3Tiny variant="outlined" onClick={handleLogout}>
                {t("layout.actions.logout")}
              </B3Tiny>

              <IconButton
                onClick={onOpenSidebar}
                sx={{
                  marginRight: "12px",
                  borderRadius: "10px",
                }}
              >
                <Iconify
                  icon="eva:menu-fill"
                  minWidth={35}
                  minHeight={35}
                  color="#2E2E2E"
                />
              </IconButton>
            </Box>
          </>
        )}
      </Grid>
    </AppBar>
  );
}
